import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';

const LocationContext = createContext();

export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
};

export const LocationProvider = ({ children }) => {
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  const fetchLocations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Pobierz lokalizacje dostępne dla użytkownika (z kontrolą uprawnień)
      const response = await api.get('/auth/available-locations');
      
      // Jeśli 401 - użytkownik nie jest zalogowany, to OK - poczekamy na zalogowanie
      if (response.status === 401) {
        setAvailableLocations([]);
        setLoading(false);
        return;
      }
const data = response.data;

      // Obsługa formatu odpowiedzi z auth/available-locations
      let locationsArray = [];
      if (data.data?.locations) {
        locationsArray = data.data.locations;
        setIsAdmin(data.data.is_admin || false);
      }
      
      setAvailableLocations(locationsArray);
      
      // Jeśli użytkownik ma dostęp tylko do jednej lokalizacji, wybierz ją automatycznie
      if (locationsArray.length === 1) {
        const onlyLocation = locationsArray[0];
        setSelectedLocation(onlyLocation);
        localStorage.setItem('selectedLocation', JSON.stringify(onlyLocation));

        // Synchronizuj z sesją backendową
        try {
          await api.post('/auth/current-location', { location_id: onlyLocation.id });
        } catch (err) {
          // session location sync failed
        }
      }
    } catch (error) {
      console.error('❌ Error fetching locations:', error);
      setError('Błąd podczas pobierania lokalizacji');
    } finally {
      setLoading(false);
    }
  }, []);

  const changeLocation = useCallback(async (locationId) => {
    const location = availableLocations.find(loc => loc.id === parseInt(locationId));
    if (location) {
      setSelectedLocation(location);
      localStorage.setItem('selectedLocation', JSON.stringify(location));

      // Powiadom backend o zmianie lokalizacji (zapisz w sesji)
      try {
        await api.post('/auth/current-location', { location_id: parseInt(locationId)
        });
      } catch (err) {
        // session location sync failed
      }
    } else {
      // location not found for given ID
    }
  }, [availableLocations]);

  useEffect(() => {
    // Najpierw wczytaj z localStorage
    const saved = localStorage.getItem('selectedLocation');
    if (saved) {
      try {
        const location = JSON.parse(saved);
        setSelectedLocation(location);
      } catch (error) {
        console.error('Błąd parsowania zapisanej lokalizacji:', error);
      }
    }
    
    // Potem pobierz aktualne dane z API
    fetchLocations();
  }, []); // Pusty dependency array - tylko przy mount

  // Sprawdź czy zapisana lokalizacja jest nadal dostępna po załadowaniu listy
  useEffect(() => {
    if (availableLocations.length > 0 && selectedLocation) {
      const isLocationAvailable = availableLocations.some(
        loc => loc.id === selectedLocation.id
      );
      
      if (!isLocationAvailable) {
        // saved location no longer available, clearing
        setSelectedLocation(null);
        localStorage.removeItem('selectedLocation');
      }
    }
  }, [availableLocations, selectedLocation]);

  const value = useMemo(() => ({
    selectedLocation,
    setSelectedLocation,
    locationId: selectedLocation?.id || null,
    availableLocations,
    loading,
    error,
    isAdmin,
    changeLocation,
    refreshLocations: fetchLocations
  }), [selectedLocation, availableLocations, loading, error, isAdmin, changeLocation, fetchLocations]);

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
};

export default LocationContext;
